// Tiny hook around fetchCurrentUser(). We deliberately don't pull in
// React Query / SWR yet — a single GET on dashboard mount doesn't
// justify the dependency, and this keeps the hook trivially testable.
//
// If/when more cached endpoints land we should swap this internal
// implementation for React Query in one place rather than in every
// feature module.

import { useEffect, useState } from 'react';
import { AxiosError } from 'axios';

import { fetchCurrentUser, type UserProfile } from '../../api/users';

export type CurrentUserState =
    | { status: 'loading'; user: null; error: null }
    | { status: 'ready'; user: UserProfile; error: null }
    | { status: 'error'; user: null; error: string };

export function useCurrentUser(): CurrentUserState {
    const [state, setState] = useState<CurrentUserState>({
        status: 'loading',
        user: null,
        error: null,
    });

    useEffect(() => {
        let cancelled = false;
        fetchCurrentUser()
            .then((user) => {
                if (!cancelled) setState({ status: 'ready', user, error: null });
            })
            .catch((err: unknown) => {
                if (cancelled) return;

                // 401s are already redirected by the axios interceptor —
                // the rejection here just means we should stop showing
                // the spinner. For everything else surface a friendly
                // message so the UI can render an error card.
                if (err instanceof AxiosError && err.response?.status === 401) {
                    return;
                }
                const message =
                    err instanceof AxiosError && err.response?.status === 403
                        ? 'Your membership level does not include Forge Ultra access.'
                        : 'Could not load your profile. Please try again.';

                setState({ status: 'error', user: null, error: message });
            });

        return () => {
            cancelled = true;
        };
    }, []);

    return state;
}
